/**
 * 华为浏览器高考频道 JS-SDK
 * 用于获取用户信息，支持华为账号认证
 */

import type { UserInfo, AuthCodeResult, SDKConfig, SameScoreDestinations } from './schemas'

interface ApiResponse<T = UserInfo> {
  code: number
  msg: string
  body: T
}

// 声明浏览器扩展的 JSBridge 接口（兼容旧版本）
declare global {
  interface Window {
    hwBrowserFeedsKitApi?: {
      base: {
        init: () => void
      }
      getSubAppAuthCode: (
        params: string,
        success: (result: string) => void,
        fail: (error: string) => void
      ) => void
    }
    hwbr?: {
      mcpAccount: {
        getSubAuthCode: (
          params: string,
          success: (result: string) => void,
          fail: (error: string) => void
        ) => void
        getSubAppAuthCode: (
          params: { subAppId: string },
          success: (result: string) => void,
          fail: (error: { errorCode: number; errorInfo: string }) => void
        ) => void
      }
    }
  }
}

// ========== 内置配置 ==========
const DEFAULT_SUB_APP_ID = '117164165'  // TODO: 替换为实际的 subAppId
const DEFAULT_API_ENDPOINT = 'https://mnzy.careert.com/api'  // TODO: 替换为实际的 API 地址

// Token 有效期（2天，单位毫秒）
const TOKEN_EXPIRE_TIME = 2 * 24 * 60 * 60 * 1000

/**
 * 华为浏览器高考频道 SDK
 */
export class GaokaoSDK {
  private config: Required<Omit<SDKConfig, 'simulationToken' | 'getAuthCode'>> & Pick<SDKConfig, 'simulationToken' | 'getAuthCode'>
  
  // Token 管理
  private token?: string
  private tokenExpireAt?: number
  private loginPromise?: Promise<UserInfo>  // 防止并发登录

  constructor(config: SDKConfig = {}) {
    this.config = {
      subAppId: DEFAULT_SUB_APP_ID,
      apiEndpoint: DEFAULT_API_ENDPOINT,
      timeout: 10000,
      simulationToken: undefined,
      ...config
    }
  }

  // ==================== 公开方法 ====================

  /**
   * 设置配置
   * @param config 配置项
   */
  setConfig(config: SDKConfig): void {
    this.config = {
      ...this.config,
      ...config,
    }
  }

  /**
   * 检查是否在华为浏览器环境中
   * simulationToken 或配置了 getAuthCode 时返回 true
   */
  isInHwBrowser(): boolean {
    // simulationToken 或 getAuthCode 配置时跳过环境验证
    if (this.config.simulationToken || this.config.getAuthCode) {
      return true
    }
    // 检测华为浏览器环境
    return false
  }

  /**
   * 检查当前登录状态
   * @returns 是否已登录且 token 有效
   */
  isLoggedIn(): boolean {
    // simulationToken 模式始终视为已登录
    if (this.config.simulationToken) {
      return true
    }
    
    // 检查 token 是否存在且未过期
    return !!this.token && !!this.tokenExpireAt && Date.now() < this.tokenExpireAt
  }

  /**
   * 获取 authCode
   * @returns Promise<AuthCodeResult>
   */
  async getAuthCode(): Promise<AuthCodeResult> {
    // simulationToken 模式返回空 authCode
    if (this.config.simulationToken) {
      return { authCode: '' }
    }

    // 检查环境或配置
    if (!this.isInHwBrowser()) {
      throw new Error('当前环境不支持华为浏览器SDK，请确保在华为浏览器中打开或配置 getAuthCode')
    }

    return this.getAuthCodeFromBrowser()
  }

  /**
   * 从浏览器环境获取 authCode
   * @returns Promise<AuthCodeResult>
   */
  private async getAuthCodeFromBrowser(): Promise<AuthCodeResult> {
    const params = { subAppId: this.config.subAppId }
    
    // 优先使用配置的 getAuthCode 函数
    const getSubAppAuthCode = this.config.getAuthCode
    
    if (!getSubAppAuthCode) {
      throw new Error('未找到华为浏览器 JSBridge 接口，请配置 getAuthCode')
    }

    // 设置超时
    const timeoutPromise = new Promise<AuthCodeResult>((_, reject) => {
      setTimeout(() => reject(new Error(`获取 authCode 超时（${this.config.timeout}ms）`)), this.config.timeout)
    })

    // 调用 getAuthCode（Promise 格式，传参 { subAppId }）
    const authCodePromise = getSubAppAuthCode(params)

    try {
      const result = await Promise.race([authCodePromise, timeoutPromise])
      return result
    } catch (error) {
      throw error instanceof Error ? error : new Error('获取 authCode 失败')
    }
  }

  /**
   * 登录
   * 自动完成：获取 authCode -> 换取用户信息 -> 保存 token
   * 
   * @returns Promise<UserInfo>
   */
  async login(): Promise<UserInfo> {
    // simulationToken 模式：模拟登录，返回空 UserInfo
    if (this.config.simulationToken) {
      this.token = this.config.simulationToken
      // simulationToken 不过期，设置一个很久的过期时间
      this.tokenExpireAt = Date.now() + 365 * 24 * 60 * 60 * 1000 // 1年
      return {} as UserInfo
    }

    // 防止并发登录
    if (this.loginPromise) {
      return this.loginPromise
    }

    this.loginPromise = this.doLogin()
    
    try {
      return await this.loginPromise
    } finally {
      this.loginPromise = undefined
    }
  }

  /**
   * 获取用户信息
   * 自动确保已登录
   * 
   * @param nowTime 可选时间戳参数，用于请求参数
   * @returns Promise<UserInfo>
   */
  async getUserInfo(nowTime?: number): Promise<UserInfo> {
    await this.ensureLoggedIn()
    
    const url = nowTime 
      ? `${this.config.apiEndpoint}/huaweixy/getUserInfoAndRecommendNum?nowTime=${nowTime}`
      : `${this.config.apiEndpoint}/huaweixy/getUserInfoAndRecommendNum`
    
    const response = await this.request<ApiResponse<UserInfo>>({
      url,
      method: 'GET',
    })
    
    return response.body
  }

  /**
   * 获取同分去向
   * 自动确保已登录
   * 
   * @returns Promise<SameScoreDestinations>
   */
  async getSameScoreDestinations(): Promise<SameScoreDestinations> {
    await this.ensureLoggedIn()
    
    const response = await this.request<ApiResponse<SameScoreDestinations>>({
      url: `${this.config.apiEndpoint}/huaweixy/sameScore`,
      method: 'GET',
    })
    
    return response.body
  }

  /**
   * 登出
   * 清除本地存储的 token
   */
  logout(): void {
    this.clearToken()
  }

  // ==================== 私有方法 ====================

  /**
   * 执行登录流程
   */
  private async doLogin(): Promise<UserInfo> {
    // 1. 获取 authCode
    const { authCode } = await this.getAuthCode()
    
    if (!authCode) {
      throw new Error('获取 authCode 失败：authCode 为空')
    }

    // 2. 用 authCode 换取用户信息
    const response = await fetch(`${this.config.apiEndpoint}/v1/hwxy/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code: authCode }),
    })

    if (!response.ok) {
      throw new Error(`登录失败: ${response.status}`)
    }

    const data: ApiResponse<UserInfo> = await response.json()
    const userInfo = data?.body

    if (!userInfo?.token) {
      throw new Error('登录失败：未返回 token')
    }

    // 3. 保存 token
    this.token = userInfo.token
    this.tokenExpireAt = Date.now() + TOKEN_EXPIRE_TIME
    this.saveToken()

    return userInfo
  }

  /**
   * 确保已登录（有有效 token）
   */
  private async ensureLoggedIn(): Promise<void> {
    // simulationToken 模式
    if (this.config.simulationToken) {
      this.token = this.config.simulationToken
      return
    }

    // 已登录且 token 有效
    if (this.isLoggedIn()) {
      return
    }

    // 需要重新登录
    await this.login()
  }

  /**
   * 带自动重试的请求（token 过期时自动重新登录）
   */
  private async request<T>(options: {
    url: string
    method: string
    body?: Record<string, unknown>
  }): Promise<T> {
    const maxRetry = 1
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= maxRetry; attempt++) {
      try {
        await this.ensureLoggedIn()

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }

        if (this.token) {
          headers['token'] = this.token
        }

        const response = await fetch(options.url, {
          method: options.method,
          headers,
          body: JSON.stringify(options.body),
        })

        if (!response.ok) {
          throw new Error(`请求失败: ${response.status}`)
        }

        const data: T = await response.json()
        
        // 检查业务错误码是否表示 token 过期
        // TODO: 根据实际错误码调整
        if (this.isTokenExpiredError(data)) {
          throw new Error('TOKEN_EXPIRED')
        }

        return data
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))
        
        // 如果是 token 过期错误，清除 token 并重试
        if (lastError.message === 'TOKEN_EXPIRED') {
          this.clearToken()
          continue
        }
        
        // 其他错误直接抛出
        throw lastError
      }
    }

    throw lastError || new Error('登录已过期，请重新登录')
  }

  /**
   * 检查响应是否表示 token 过期
   */
  private isTokenExpiredError(data: unknown): boolean {
    if (typeof data !== 'object' || data === null) return false
    
    const response = data as { code?: number }
    // Token 过期或无效的错误码
    return response.code === 403 || response.code === 503 || response.code === 504
  }

  /**
   * 保存 token 到内存
   */
  private saveToken(): void {
    // token 仅保存在内存中，不存储到 localStorage
  }

  /**
   * 清除 token（仅内存）
   */
  private clearToken(): void {
    this.token = undefined
    this.tokenExpireAt = undefined
  }
}

// 导出类型
export type { UserInfo, AuthCodeResult, SDKConfig, SameScoreDestinations }

// 创建默认实例
const sdk = new GaokaoSDK()

// 导出实例（唯一入口）
export default sdk